import { Device } from "Types";
import { useState, useRef, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Klydo } from "../../api/klydo";
import useAllKlydoLabels from "../../views/Klydos/hooks/useAllKlydoLabels";
import { Label } from "../../api/label";
import DSBox from "../../views/system-design/DSBox";
import DSDialog from "../../views/system-design/DSDialog";
import DSDialogContent from "../../views/system-design/DSDialogContent";
import DSTextField from "../../views/system-design/DSTextField";
import DSTypography from "../../views/system-design/DSTypography";
import DSIconButton from "../../views/system-design/DSIconButton";
import DSCircularProgress from "../../views/system-design/DSCircularProgress";

function TagList(props: {
  klydo?: Klydo;
  device?: Device;
  onSave: (list: string[]) => Promise<void>;
}) {
  const [show, setShow] = useState(false);
  const { data: labelsData, isLoading: labelsLoading } = useAllKlydoLabels();
  const [allTags, setAllTags] = useState<Array<Label>>();
  const [myTags, setMyTags] = useState(() => {
    if (props.klydo) return props.klydo.tags ?? ([] as Array<string>);
    return props.device?.premium ?? ([] as Array<string>);
  });
  const [filteredTags, setFilteredTags] = useState<Array<Label>>();
  const [autoTags, setAutoTags] = useState<Array<Label>>();
  const [toAddTag, setToAddTag] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (labelsData) {
      setAllTags([...labelsData]);
      setFilteredTags([...labelsData]);
      setAutoTags([...labelsData]);
    }
    if (props.klydo) setMyTags(props.klydo.tags!);
  }, [show, labelsData]);
  useEffect(() => {
    setFilteredTags(
      allTags?.filter(
        (tag) =>
          !myTags?.includes(tag.text) &&
          (toAddTag === "" ||
            tag.text.toLowerCase().includes(tag.text.toLowerCase())),
      ),
    );
  }, [myTags, toAddTag, allTags]);
  const target = useRef(null);
  const tagStyel = (s: string, i: number) => (
    <DSBox
      key={i}
      sx={{
        cursor: "pointer",
        display: "flex",
        margin: "5px",
        padding: "1px 6px",
        alignItems: "center",
        bgcolor: "white.16",
        borderRadius: "50vw",
        height: "fit-content",
      }}
    >
      <DSTypography
        sx={{
          margin: 0,
          padding: "2px",
          fontSize: "11px",
          color: "text.primary",
        }}
      >
        {s}
      </DSTypography>
    </DSBox>
  );

  const addTag = (s: string) => {
    const list = s
      .split(",")
      ?.map((l) => l.trim())
      ?.filter(
        (f) =>
          f !== "" &&
          !myTags?.map((m) => m.toLowerCase())?.includes(f.toLowerCase()),
      );
    if (list.length === 0) return;
    setMyTags([...(myTags ?? []), ...list]);
  };

  const removeTag = (s: string) => {
    const n = myTags.findIndex((f) => f === s);
    if (n === -1) {
      console.log("NOT SPOS 2 HAPPEN");
      return;
    }
    const tmpMyTags = myTags?.filter((_, i) => i !== n);
    setMyTags([...tmpMyTags]);
  };
  return (
    <>
      <DSBox>
        <DSBox
          sx={{ display: "flex", flexWrap: "wrap", flex: "1 1 auto" }}
          ref={target}
          onClick={(e) => {
            e.stopPropagation();
            setShow(!show);
          }}
        >
          {(props.klydo ? props.klydo.tags : props.device?.premium)?.map(
            tagStyel,
          )}
          {((props.klydo &&
            (!props.klydo.tags || props.klydo.tags.length === 0)) ||
            (props.device &&
              (props.device.premium === undefined ||
                props.device.premium.length === 0))) && (
            <DSBox
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "5px",
                bgcolor: "white.16",
                alignSelf: "center",
                borderRadius: "50%",
              }}
            >
              <AddIcon sx={{ fontSize: 12 }} />
            </DSBox>
          )}
          {!!props.klydo?.tags?.length && navigator?.clipboard && (
            <DSBox
              title="Copy tags"
              sx={{
                position: "absolute",
                right: "1px",
                top: "25px",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard
                  .writeText(props.klydo!.tags!.join(","))
                  .then(() => {
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 2000);
                  })
                  .catch(console.log);
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              )}
            </DSBox>
          )}
        </DSBox>
        {show && (props.device || props.klydo) && (
          <DSDialog
            sx={{
              zIndex: 9100,
            }}
            open={true}
            onClose={() => setShow(false)}
            maxWidth="xs"
            fullWidth
          >
            <DSDialogContent
              tabIndex={1}
              onKeyDown={(ev) => {
                ev.stopPropagation();
                if (ev.key !== "Enter") return;
                ev.preventDefault();
                if (toAddTag === "")
                  props.onSave(myTags).then(() => setShow(false));
                else addTag(toAddTag);
                setToAddTag("");
              }}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                padding: "10px",
              }}
            >
              <DSBox sx={{ display: "flex", flexDirection: "column" }}>
                <DSBox sx={{ display: "flex", flexDirection: "row" }}>
                  <form
                    onSubmit={(a) => {
                      if (props.klydo) {
                        a.preventDefault();
                        if (toAddTag === "") return;
                        else addTag(toAddTag);
                        setToAddTag("");
                      }
                    }}
                  >
                    <DSTextField
                      size="small"
                      onKeyDown={(ev) => {
                        if (toAddTag === "") return;
                        ev.stopPropagation();
                        if (ev.key === "Enter") {
                          addTag(toAddTag);
                          setToAddTag("");
                        }
                      }}
                      autoFocus
                      placeholder={props.klydo ? "New tag" : "Search"}
                      value={toAddTag}
                      sx={{
                        margin: "5px",
                        maxWidth: "350px",
                        width:
                          toAddTag === ""
                            ? "120px"
                            : "calc(24px + 12px *" + toAddTag.length + ")",
                      }}
                      onChange={(e) => {
                        const autoCTags = filteredTags?.filter((mt) =>
                          mt.text?.includes(e.target.value),
                        );
                        if (autoCTags?.length) {
                          setAutoTags(autoCTags);
                        } else setAutoTags([]);
                        setToAddTag(e.target.value);
                      }}
                    />
                  </form>
                  {props.klydo && (
                    <DSIconButton
                      title="add"
                      size="small"
                      sx={{
                        bgcolor: "white.16",
                        alignSelf: "center",
                      }}
                      onClick={() => {
                        if (toAddTag === "") return;
                        else addTag(toAddTag);
                        setToAddTag("");
                      }}
                    >
                      <AddIcon sx={{ fontSize: 12 }} />
                    </DSIconButton>
                  )}
                </DSBox>
                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                  }}
                >
                  <DSTypography sx={{ fontSize: 11, padding: "5px" }}>
                    {props.klydo ? "Klydo tags:" : "Tags:"}
                  </DSTypography>
                  {myTags?.map((t, i) => (
                    <DSBox
                      key={i}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTag(t);
                      }}
                    >
                      {tagStyel(t, i)}
                    </DSBox>
                  ))}
                </DSBox>
              </DSBox>
              <DSBox
                sx={{
                  height: "1px",
                  width: "100%",
                  bgcolor: "divider",
                }}
              ></DSBox>
              <DSBox
                sx={{
                  maxHeight: 350,
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  overflow: "auto",
                }}
              >
                <DSTypography sx={{ fontSize: 11, padding: "5px" }}>
                  All tags:
                </DSTypography>
                {labelsLoading ? (
                  <DSCircularProgress size={20} />
                ) : filteredTags ? (
                  (autoTags!.length ? autoTags! : filteredTags!)?.map(
                    (t, i) => (
                      <DSBox
                        key={i}
                        onClick={() => {
                          addTag(t.text);
                        }}
                      >
                        {tagStyel(t.text, i)}
                      </DSBox>
                    ),
                  )
                ) : (
                  <DSCircularProgress size={20} />
                )}
              </DSBox>
              <DSBox
                sx={{
                  height: "1px",
                  width: "100%",
                  bgcolor: "divider",
                }}
              ></DSBox>
              <DSBox
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <DSIconButton
                  sx={{
                    margin: "5px",
                    bgcolor: "error.main",
                    color: "common.white",
                    "&:hover": { bgcolor: "error.dark" },
                  }}
                  onClick={() => {
                    setMyTags(
                      props.klydo
                        ? (props.klydo.tags ?? ([] as Array<string>))
                        : (props.device?.premium ?? ([] as Array<string>)),
                    );
                    setShow(false);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </DSIconButton>
                <DSIconButton
                  sx={{
                    margin: "5px",
                    bgcolor: "success.main",
                    color: "common.white",
                    "&:hover": { bgcolor: "success.dark" },
                  }}
                  onKeyDown={(ev) => {
                    ev.stopPropagation();
                    if (ev.key === "Enter")
                      props.onSave(myTags).then(() => setShow(false));
                  }}
                  onClick={() => {
                    props.onSave(myTags).then(() => setShow(false));
                  }}
                >
                  <CheckIcon fontSize="small" />
                </DSIconButton>
              </DSBox>
            </DSDialogContent>
          </DSDialog>
        )}
      </DSBox>
    </>
  );
}

export default TagList;
